import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from 'src/app/shared/services/auth.service';
import { ToastrService } from 'ngx-toastr';
import { DownloadsService, DownloadFile } from 'src/app/shared/services/downloads.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { NgxSpinnerService } from 'ngx-spinner';
import { UploadApkModalComponent } from './upload-apk-modal/upload-apk-modal.component';
import * as moment from 'moment';
import Swal from 'sweetalert2';

interface AndroidBuild {
  key: string;
  fileName: string;
  url: string;
  size: number;
  lastModified: string;
  version?: string;
  buildDate?: string;
}

@Component({
  selector: 'app-downloads',
  templateUrl: './downloads.component.html',
  styleUrls: ['./downloads.component.scss'],
})
export class DownloadsComponent implements OnInit {
  tableHeaders = ['Name', 'Build Date', 'URL', 'Actions'];
  androidBuilds: AndroidBuild[] = [];
  userRole: string;

  constructor(
    private router: Router,
    private authService: AuthService,
    private toastr: ToastrService,
    private downloadsService: DownloadsService,
    private modalService: NgbModal,
    private spinnerService: NgxSpinnerService
  ) {}

  async ngOnInit(): Promise<void> {
    const data = JSON.parse(localStorage.getItem('user'));
    if (!!!data) this.authService.logout();

    this.userRole = await this.authService.getUserRole();

    // Only allow practice_admin to access this module
    if (this.userRole !== 'practice_admin') {
      this.router.navigate(['/dashboard']);
      return;
    }

    this.loadAndroidBuilds();
  }

  async loadAndroidBuilds(): Promise<void> {
    this.spinnerService.show();
    try {
      const files = await this.downloadsService.getAllDownloadFiles();
      this.androidBuilds = files.map((file: DownloadFile) => {
        // Extract version from filename if possible (e.g., app-v1.0.0.apk -> 1.0.0)
        const versionMatch = file.fileName.match(/v?(\d+\.\d+\.\d+)/i);
        const version = versionMatch ? versionMatch[1] : undefined;

        // Format date
        const buildDate = file.lastModified
          ? moment(file.lastModified).format('YYYY-MM-DD')
          : undefined;

        return {
          key: file.key,
          fileName: file.fileName,
          url: file.url,
          size: file.size,
          lastModified: file.lastModified,
          version,
          buildDate,
        };
      });
      this.spinnerService.hide();
    } catch (err: any) {
      this.spinnerService.hide();
      console.error('Error loading Android builds:', err);
      this.toastr.error(err?.error?.message || err?.message || 'Failed to load APK files');
    }
  }

  openUploadModal(): void {
    const modalRef = this.modalService.open(UploadApkModalComponent, {
      size: 'lg',
      centered: true,
    });

    modalRef.result.then(
      (result) => {
        if (result) {
          // Reload the list after successful upload
          this.loadAndroidBuilds();
        }
      },
      (dismissed) => {
        // Modal was dismissed
      }
    );
  }

  copyUrl(build: AndroidBuild): void {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard
        .writeText(build.url)
        .then(() => {
          this.toastr.success('URL copied to clipboard!');
        })
        .catch((err) => {
          console.error('Failed to copy URL:', err);
          this.fallbackCopyToClipboard(build.url);
        });
    } else {
      this.fallbackCopyToClipboard(build.url);
    }
  }

  private fallbackCopyToClipboard(text: string): void {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    textArea.style.top = '-999999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    try {
      document.execCommand('copy');
      this.toastr.success('URL copied to clipboard!');
    } catch (err) {
      console.error('Fallback copy failed:', err);
      this.toastr.error('Failed to copy URL');
    }
    document.body.removeChild(textArea);
  }

  downloadUrl(build: AndroidBuild): void {
    const link = document.createElement('a');
    link.href = build.url;
    link.download = build.fileName;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    this.toastr.success('Download started!');
  }

  deleteApkFile = (build: AndroidBuild): void => {
    Swal.fire({
      title: 'Are you sure?',
      text: `You are about to delete "${build.fileName}". This action cannot be undone!`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel',
    }).then(async (result) => {
      if (result.isConfirmed) {
        this.spinnerService.show();
        try {
          await this.downloadsService.deleteDownloadFile(build.key);
          this.spinnerService.hide();
          Swal.fire('Deleted!', 'APK file has been deleted successfully.', 'success');
          // Reload the list after successful deletion
          this.loadAndroidBuilds();
        } catch (err: any) {
          this.spinnerService.hide();
          console.error('Error deleting APK file:', err);
          Swal.fire(
            'Error!',
            err?.error?.message || err?.message || 'Failed to delete APK file',
            'error'
          );
        }
      }
    });
  };
}

